import { useCallback, useEffect } from 'react';

import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Alert } from '@pulse-web-ui/alert';
import { TooltipDirection, TriggerDirection } from '@pulse-web-ui/tooltip';
import { observer } from 'mobx-react-lite';

import {
  AGENT_WORK_SUPPORT_EMAIL,
  AgentEmploymentType,
  AgentType,
  AutocompleteSelectController,
  DatepickerController,
  FormControlContainer,
  InputController,
  SelectController,
  TextLink,
  TextWithTooltip,
  useStores,
} from '@shared/index';

import { useGetAgentEmploymentDetailsBranchOptions } from '../../lib';
import type { AgentEmploymentDetailsFormParams } from '../../model';
import { OGRNIP_MASK } from '../../model/constants';

interface AgentEmploymentDetailsProps {
  minDateIpRegistration: Date;
  isOnlineRegistrationBlocked?: boolean;
  setIsOnlineRegistrationBlocked: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AgentEmploymentDetails = observer(
  ({
    minDateIpRegistration,
    isOnlineRegistrationBlocked,
    setIsOnlineRegistrationBlocked,
  }: AgentEmploymentDetailsProps) => {
    const { t } = useTranslation();
    const {
      MainStore: {
        agentContractStore: { profile },
      },
    } = useStores();

    const agentOptions = [
      {
        label: t('AGENT_EMPLOYMENT_DETAILS:employmentTypeOptions.rgs'),
        value: AgentEmploymentType.RGS,
      },
      {
        label: t('AGENT_EMPLOYMENT_DETAILS:employmentTypeOptions.external'),
        value: AgentEmploymentType.EXTERNAL,
      },
    ];

    const agentTypeOptions = [
      {
        label: t('AGENT_EMPLOYMENT_DETAILS:typeOptions.IP'),
        value: AgentType.IP,
      },
      {
        label: t('AGENT_EMPLOYMENT_DETAILS:typeOptions.FL'),
        value: AgentType.FL,
      },
    ];

    const {
      control,
      formState: { errors },
      clearErrors,
      setValue,
      watch,
    } = useFormContext<AgentEmploymentDetailsFormParams>();

    const { branchOptions, blockedBranchOptions, defaultBranchCode } =
      useGetAgentEmploymentDetailsBranchOptions();

    useEffect(() => {
      if (!!branchOptions.length && !!defaultBranchCode) {
        setValue('branchCode', defaultBranchCode);
        setIsOnlineRegistrationBlocked(blockedBranchOptions[defaultBranchCode]);
      }
    }, [defaultBranchCode, branchOptions.length, blockedBranchOptions]);

    const isImported = profile?.isImported;
    const isIP = watch('agentType') === AgentType.IP;

    const maxDateIpRegistration = new Date();

    const isBranchCodeDisabled = isImported && !!defaultBranchCode;

    const handleBranchCodeChange = useCallback(
      (branchCode?: string) => {
        if (errors) {
          clearErrors();
        }

        if (branchCode) {
          setIsOnlineRegistrationBlocked(blockedBranchOptions[branchCode]);
        }
      },
      [errors, blockedBranchOptions]
    );

    return (
      <>
        <FormControlContainer>
          <AutocompleteSelectController
            control={control}
            label={t('AGENT_EMPLOYMENT_DETAILS:labels.branchCode') || ''}
            name='branchCode'
            disabled={isBranchCodeDisabled}
            testId='agentEmploymentDetails.branchCode'
            options={branchOptions}
            onChange={handleBranchCodeChange}
            isShowHintWhenNotDirty
            hint={
              !isImported
                ? t('AGENT_EMPLOYMENT_DETAILS:hints.branchCode')
                : undefined
            }
          />
        </FormControlContainer>
        {isOnlineRegistrationBlocked ? (
          <Alert
            type='warning'
            description={
              <Trans
                values={{ href: AGENT_WORK_SUPPORT_EMAIL }}
                components={{ a: <TextLink /> }}
              >
                {t('AGENT_EMPLOYMENT_DETAILS:alert.description')}
              </Trans>
            }
          />
        ) : (
          <>
            <FormControlContainer>
              <SelectController
                options={agentOptions}
                control={control}
                name='employmentType'
                label={t('AGENT_EMPLOYMENT_DETAILS:labels.employmentType')}
                testId='agentEmploymentDetails.employmentType'
                disabled={isImported}
              />
              <TextWithTooltip
                text={t('AGENT_EMPLOYMENT_DETAILS:hints.employmentTypeTitle')}
                tooltipText={t('AGENT_EMPLOYMENT_DETAILS:hints.employmentType')}
                direction={TooltipDirection.DOWN}
                trigger={TriggerDirection.CLICK}
              />
            </FormControlContainer>
            <FormControlContainer>
              <SelectController
                options={agentTypeOptions}
                control={control}
                name='agentType'
                label={t('AGENT_EMPLOYMENT_DETAILS:labels.agentType')}
                testId='agentEmploymentDetails.agentType'
                disabled={isImported}
              />
            </FormControlContainer>
            {isIP && (
              <>
                <FormControlContainer>
                  <InputController
                    control={control}
                    name='ogrnIp'
                    id='ogrnIp'
                    label={t('AGENT_EMPLOYMENT_DETAILS:labels.ogrnIp')}
                    placeholder='000000000000000'
                    mask={OGRNIP_MASK}
                    testId='agentEmploymentDetails.ogrnIp'
                  />
                </FormControlContainer>
                <FormControlContainer>
                  <DatepickerController
                    control={control}
                    name='dateIpRegistration'
                    label={t(
                      'AGENT_EMPLOYMENT_DETAILS:labels.dateIpRegistration'
                    )}
                    minDate={minDateIpRegistration}
                    maxDate={maxDateIpRegistration}
                    testId='agentEmploymentDetails.dateIpRegistration'
                    showMonthDropdown
                    showYearDropdown
                    showInput
                  />
                </FormControlContainer>
              </>
            )}
          </>
        )}
      </>
    );
  }
);
